import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn, inputVariants, type InputVariants } from '../../../utils';

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'>,
    InputVariants {
  /** Error message to display below the input */
  error?: string;
  /** Label text to display above the input */
  label?: string;
  /** Whether to show the password visibility toggle button (default: true) */
  showToggle?: boolean;
  /** Accessible label for show password button */
  showPasswordLabel?: string;
  /** Accessible label for hide password button */
  hidePasswordLabel?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      size,
      hasError,
      hasIcon,
      error,
      label,
      id,
      showToggle = true,
      showPasswordLabel = 'Show password',
      hidePasswordLabel = 'Hide password',
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
          >
            {label}
          </label>
        )}
        {/* agn-password-field / agn-password-toggle: under a finger in the next
            interface the toggle is 44x44 beside the field (next-components.css,
            agion/Agion#2966); elsewhere they style nothing. */}
        <div className={cn('relative', showToggle && 'agn-password-field')}>
          <input
            ref={ref}
            id={inputId}
            type={showPassword ? 'text' : 'password'}
            className={cn(
              inputVariants({ size, hasError: hasError || !!error, hasIcon: showToggle ? 'right' : hasIcon }),
              showToggle && 'pr-10',
              className
            )}
            aria-invalid={hasError || !!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            disabled={disabled}
            {...props}
          />
          {showToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={disabled}
              className={cn(
                'agn-password-toggle absolute right-0 top-0 h-full px-3',
                'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300',
                'transition-colors duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-0 dark:focus-visible:ring-white',
                'rounded-r-lg',
                disabled && 'opacity-50 cursor-not-allowed hover:text-slate-500'
              )}
              aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
            {error}
          </p>
        )}
      </div>
    );
  }
);
PasswordInput.displayName = 'PasswordInput';
